import { useEffect } from "react";
import PolicyLayout from "../components/PolicyLayout";

/**
 * ChatVilkaarPage — public Terms of Service for the Cleany Chat app.
 * Served at /chat/vilkaar. Content is a 1:1 rendering of the canonical
 * TERMS.md that ships with the chat codebase (senest ændret 2026-09-02).
 */
export default function ChatVilkaarPage() {
    useEffect(() => {
        document.title = "Vilkår for Cleany Chat · Cleany.dk";
    }, []);

    return (
        <PolicyLayout title="Vilkår for brug af Cleany Chat" lastUpdated="2. september 2026">
            <p className="lead">
                Cleany Chat er et arbejdsredskab, der stilles til rådighed af Cleany
                ApS for virksomheder, der bruger Cleany. Ved at logge ind accepterer
                du disse vilkår.
            </p>

            <h2>Din konto</h2>
            <p>
                Din konto oprettes af din arbejdsgiver eller den virksomhed, du
                samarbejder med gennem Cleany. Du kan ikke oprette en konto selv, og
                du kan ikke bruge appen uden.
            </p>
            <p>
                Kontoen er personlig. Du må ikke dele dine loginoplysninger med andre
                eller bruge en andens konto.
            </p>
            <p>
                Ophører dit ansættelses- eller samarbejdsforhold, lukkes din adgang.
            </p>

            <h2>Hvad chatten må bruges til</h2>
            <p>
                Cleany Chat er til arbejdsrelateret kommunikation: opgaver,
                planlægning, spørgsmål og koordinering mellem kolleger og kunder.
            </p>
            <p>
                Du bestemmer ikke selv, hvilke grupper du er med i. De oprettes
                automatisk ud fra opgaverne i Cleany, eller manuelt af din
                organisation.
            </p>

            <h2>Hvad du ikke må</h2>
            <p>
                <strong>Der er nul tolerance for stødende indhold og krænkende
                adfærd.</strong>
            </p>
            <p>Du må ikke sende, dele eller opfordre til:</p>
            <ul>
                <li>Chikane, trusler, mobning eller anden krænkende adfærd over for andre</li>
                <li>Diskriminerende eller hadefulde ytringer</li>
                <li>Seksuelt indhold</li>
                <li>Ulovligt materiale af enhver art</li>
                <li>Indhold, du ikke har ret til at dele</li>
            </ul>
            <p>
                Du må heller ikke forsøge at få adgang til samtaler eller
                oplysninger, du ikke er tildelt, eller at omgå appens
                sikkerhedsforanstaltninger.
            </p>

            <h2>Hvis noget går galt</h2>
            <p>
                Møder du indhold eller adfærd, der ikke hører hjemme, kan du handle
                på det med det samme i appen:
            </p>
            <ul>
                <li>
                    <strong>Rapportér en besked.</strong> Hold fingeren på beskeden
                    og vælg <em>Rapportér</em>.
                </li>
                <li>
                    <strong>Rapportér en bruger.</strong> Tryk på personens navn og
                    vælg <em>Rapportér</em>.
                </li>
                <li>
                    <strong>Blokér en bruger.</strong> Tryk i søgefeltet øverst i
                    chatoversigten, søg personen frem, tryk på navnet og vælg{" "}
                    <em>Blokér</em>. Du ser ikke længere beskeder fra dem.
                </li>
            </ul>
            <p>
                Rapporteringer går til Cleany, og vi behandler dem hurtigst muligt.
            </p>
            <p>
                Du bør også orientere din nærmeste leder — chatten er et
                arbejdsredskab, og adfærd i den er et anliggende for arbejdspladsen
                på lige fod med adfærd i øvrigt.
            </p>

            <h2>Hvad vi må</h2>
            <p>
                Cleany kan fjerne indhold og lukke eller suspendere en konto uden
                varsel, hvis disse vilkår overtrædes. Det gælder også, hvis vi bliver
                bedt om det af den virksomhed, der har oprettet kontoen.
            </p>
            <p>
                Samtalerne er ikke end-to-end-krypterede, og Cleany kan derfor tilgå
                indholdet. Vi gør det ved konkret driftsbehov, eller når den
                dataansvarlige beder om det. Se{" "}
                <a href="https://cleany.dk/chat/privacy">privatlivspolitikken</a> for
                hvordan vi behandler oplysninger.
            </p>

            <h2>Tilgængelighed</h2>
            <p>
                Vi bestræber os på, at chatten er tilgængelig, men vi kan ikke
                garantere uafbrudt drift. Vi kan ændre eller indstille funktioner, og
                vi kan opdatere disse vilkår. Væsentlige ændringer varsles i appen
                eller gennem din arbejdsgiver.
            </p>

            <h2>Ansvar</h2>
            <p>
                Cleany er ikke ansvarlig for indholdet af de beskeder, brugerne
                sender. Ansvaret for det, du skriver, er dit eget.
            </p>
            <p>
                Cleany er ikke ansvarlig for indirekte tab, herunder driftstab, som
                følge af manglende tilgængelighed.
            </p>

            <h2>Lovvalg</h2>
            <p>Disse vilkår er underlagt dansk ret.</p>

            <h2>Kontakt</h2>
            <p>
                Spørgsmål til vilkårene eller rapportering af misbrug:{" "}
                <strong>
                    <a href="mailto:info@cleany.dk">info@cleany.dk</a>
                </strong>
            </p>
        </PolicyLayout>
    );
}
